<template>
  <div class="pt-2 pb-6 md:py-6">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
        <h1 class="md:text-3xl text-xl font-medium text-gray-900">
          {{ $t("products.your_created_projects") }}
        </h1>
        <div class="flex gap-3">
          <button
            type="button"
            v-on:click="navigationMenuStore.toggleSlideOverCategories"
            class="inline-flex items-center shadow-sm px-4 py-1.5 transition ease-in border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-brainspro-400 hover:text-white active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue"
          >
            {{ $t("general.categories") }}
          </button>
          <button
            type="button"
            v-on:click="productsStore.toggleModal"
            class="inline-flex items-center shadow-sm px-4 py-1.5 transition ease-in border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-brainspro-400 hover:text-white active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue"
          >
            <svg class="-ml-1.5 mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t("products.new_project") }}</span>
          </button>
        </div>
      </div>

      <div class="mt-5 w-full sm:max-w-xs">
        <label for="product-search" class="sr-only">{{ $t("products.search_label") }}</label>
        <div class="relative rounded-md shadow-sm">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
            </svg>
          </div>
          <input
            id="product-search"
            type="search"
            :value="search"
            v-on:input="onSearchInput($event.target.value)"
            class="focus:ring-bpblue focus:border-bpblue block w-full pl-10 sm:text-sm border-gray-300 rounded-md placeholder-gray-500"
            :placeholder="$t('products.search_your_project')"
          />
        </div>
      </div>

      <div ref="listTop" class="py-4" :aria-busy="loading ? 'true' : 'false'">
        <!-- Carga: esqueleto con la forma de las filas -->
        <ul v-if="loading" class="bg-white shadow sm:rounded-lg divide-y divide-gray-100" aria-hidden="true">
          <li v-for="row in perPage" :key="row" class="flex items-center gap-4 px-6 py-4">
            <div class="h-14 w-28 flex-shrink-0 rounded bg-gray-200 animate-pulse motion-reduce:animate-none"></div>
            <div class="flex-1 space-y-2">
              <div class="h-4 w-2/3 max-w-sm rounded bg-gray-200 animate-pulse motion-reduce:animate-none"></div>
              <div class="h-3 w-1/3 max-w-[10rem] rounded bg-gray-100 animate-pulse motion-reduce:animate-none"></div>
            </div>
            <div class="hidden md:block h-6 w-11 rounded-full bg-gray-200 animate-pulse motion-reduce:animate-none"></div>
          </li>
        </ul>
        <p v-if="loading" class="sr-only" role="status">{{ $t("products.loading_products") }}</p>

        <!-- Error al cargar -->
        <div v-else-if="loadError" class="rounded-lg border border-red-200 bg-red-50 px-6 py-8 text-center" role="alert">
          <p class="text-sm font-medium text-red-800">{{ $t("products.load_error") }}</p>
          <button type="button" v-on:click="fetchProducts"
                  class="mt-4 inline-flex items-center px-4 py-2 rounded-md border border-red-300 bg-white text-sm font-medium text-red-700 hover:bg-red-50 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
            {{ $t("products.retry") }}
          </button>
        </div>

        <!-- Sin resultados para la búsqueda -->
        <div v-else-if="!products.length && search" class="rounded-lg bg-white shadow px-6 py-12 text-center">
          <p class="text-sm font-medium text-gray-900">{{ $t("products.no_results_title", { search }) }}</p>
          <p class="mt-1 text-sm text-gray-600">{{ $t("products.no_results_text") }}</p>
          <button type="button" v-on:click="clearSearch"
                  class="mt-4 inline-flex items-center px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
            {{ $t("products.clear_search") }}
          </button>
        </div>

        <!-- Academia sin productos -->
        <div v-else-if="!products.length" class="rounded-lg bg-white shadow px-6 py-12 text-center">
          <p class="text-sm font-medium text-gray-900">{{ $t("products.empty_title") }}</p>
          <p class="mt-1 text-sm text-gray-600">{{ $t("products.empty_text") }}</p>
          <button type="button" v-on:click="productsStore.toggleModal"
                  class="mt-4 inline-flex items-center px-4 py-2 rounded-md border border-transparent bg-bpblue text-sm font-medium text-white shadow-sm hover:bg-blue-500 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
            {{ $t("products.new_project") }}
          </button>
        </div>

        <template v-else>
          <div class="hidden md:block shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
            <table class="min-w-full">
              <thead>
                <tr>
                  <th scope="col" class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">
                    {{ $t("products.projects_name") }}
                  </th>
                  <th scope="col" class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">
                    {{ $t("general.published") }}
                  </th>
                  <th scope="col" class="px-6 py-3 border-b border-gray-200 bg-gray-50">
                    <span class="sr-only">{{ $t("products.actions") }}</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white">
                <tr v-for="product in products" :key="product.uuid" class="hover:bg-gray-50 transition-colors ease-in">
                  <td class="px-6 py-4 border-b border-gray-200">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 h-14 relative">
                        <div v-if="product.featured" class="text-yellow-400 absolute -top-3 -left-3 bg-white rounded-full shadow-lg" :title="$t('products.featured')">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                          </svg>
                        </div>
                        <img v-if="product.thumbnail" class="h-14 w-28 object-cover rounded" :src="product.thumbnail" alt="" loading="lazy"/>
                        <div v-else class="h-14 w-28 bg-gray-400 rounded text-center">
                          <img src="/images/avatar-transparent.png" class="h-14 inline" alt=""/>
                        </div>
                      </div>
                      <div class="ml-4 min-w-0">
                        <div class="text-sm leading-5 font-medium text-gray-900">{{ product.title }}</div>
                        <ul v-if="product.custom_categories?.length" class="mt-1 flex flex-wrap gap-2">
                          <li v-for="custom_category in product.custom_categories" :key="custom_category.uuid"
                              class="text-sm leading-5 text-brainspro-800 rounded bg-brainspro-100 py-1 px-3">
                            {{ custom_category.title }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 border-b border-gray-200">
                    <ToggleComponent
                      :status="product.status === 'publish'"
                      :callback="changeProductStatus"
                      :product="product.uuid"
                      :label="$t('general.published')"
                    ></ToggleComponent>
                  </td>
                  <td class="px-6 py-4 text-right border-b border-gray-200 text-sm leading-5 font-medium">
                    <div class="flex items-center justify-end gap-2">
                      <router-link
                        :to="{ name: 'products-edit', params: { id: product.uuid } }"
                        :title="$t('products.edit_product')" :aria-label="$t('products.edit_product')"
                        class="inline-block p-1 h-7 w-7 text-gray-500 rounded-full hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-bpblue"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </router-link>
                      <a
                        :href="'https://' + product.link + '?preview=true'" target="_blank" rel="noopener"
                        :title="$t('products.preview')" :aria-label="$t('products.preview')"
                        class="inline-block p-1 h-7 w-7 text-gray-500 rounded-full hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-bpblue"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                      </a>
                      <button
                        v-if="product.projects?.uuid"
                        type="button"
                        v-on:click="goToMoodleCourse(product.projects.uuid)"
                        :disabled="classroomStore.isBusy"
                        :title="$t('products.classroom')" :aria-label="$t('products.classroom')"
                        class="p-1 h-7 w-7 text-gray-500 rounded-full hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-bpblue"
                      >
                        <svg v-if="!classroomStore.isLoading(product.projects.uuid)" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path fill="#fff" d="M12 14l9-5-9-5-9 5 9 5z"/>
                          <path fill="#fff" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"/>
                        </svg>
                        <font-awesome-icon v-else class="h-5 w-5 animate-spin" icon="spinner"/>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <mobile-products-list-component class="block md:hidden" :products="products"></mobile-products-list-component>
        </template>

        <PaginationControls
          v-if="!loadError"
          class="mt-6"
          :current-page="meta.current_page"
          :last-page="meta.last_page"
          :total="meta.total"
          :from="meta.from ?? 0"
          :to="meta.to ?? 0"
          :loading="loading"
          @change="goToPage"
        ></PaginationControls>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationControls from "@/components/navigation/PaginationControls";
import ToggleComponent from "@/components/forms/ToggleComponent";
import productService from "@/services/product/ProductService";
import { useClassroomStore } from "@/stores/classroom";
import MobileProductsListComponent from "@/views/products/Mobile/ProductsListComponent";
import NotificationService from "@/services/notification/NotificationService";
import { useProductsStore } from "@/stores/products";
import { useNavigationMenuStore } from "@/stores/navigationMenu";
import { useAcademiesStore } from "@/stores/academies";

const PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const emptyMeta = () => ({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0 });

// La página y la búsqueda viven en la query (?page=2&search=…) para poder recargar,
// compartir el enlace y volver atrás con el navegador.
const pageFromQuery = (query) => Math.max(1, parseInt(query.page, 10) || 1);

export default {
  name: "ProductList",
  components: {
    ToggleComponent,
    PaginationControls,
    MobileProductsListComponent,
  },
  setup() {
    return {
      classroomStore: useClassroomStore(),
      productsStore: useProductsStore(),
      navigationMenuStore: useNavigationMenuStore(),
      academiesStore: useAcademiesStore(),
    };
  },
  data() {
    return {
      loading: true,
      loadError: false,
      products: [],
      meta: emptyMeta(),
      perPage: PER_PAGE,
      search: this.$route.query.search ?? "",
      searchTimer: null,
      // Evita que una respuesta lenta de una página anterior pise la actual.
      requestId: 0,
    };
  },
  watch: {
    "$route.query"(query, previous) {
      if (this.$route.name !== "products") {
        return;
      }
      if (query.page !== previous?.page || query.search !== previous?.search) {
        this.search = query.search ?? "";
        this.fetchProducts();
      }
    },
  },
  created() {
    this.fetchProducts();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    fetchProducts() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.loadError = false;

      productService
        .paginate({
          academyUuid: this.academiesStore.academy.uuid,
          search: this.$route.query.search ?? "",
          page: pageFromQuery(this.$route.query),
          perPage: this.perPage,
        })
        .then(({ data }) => {
          if (requestId !== this.requestId) {
            return;
          }
          this.products = data.data ?? [];
          this.meta = { ...emptyMeta(), ...data.meta };

          // Si la página pedida ya no existe (p. ej. tras borrar), se salta a la última.
          if (!this.products.length && this.meta.current_page > this.meta.last_page && this.meta.last_page >= 1) {
            this.updateQuery({ page: this.meta.last_page }, true);
          }
        })
        .catch(() => {
          if (requestId === this.requestId) {
            this.loadError = true;
          }
        })
        .finally(() => {
          if (requestId === this.requestId) {
            this.loading = false;
          }
        });
    },
    updateQuery(changes, replace = false) {
      const query = { ...this.$route.query, ...changes };
      Object.keys(query).forEach((key) => {
        if (query[key] === "" || query[key] === null || (key === "page" && Number(query[key]) === 1)) {
          delete query[key];
        }
      });
      return this.$router[replace ? "replace" : "push"]({ name: "products", query });
    },
    goToPage(page) {
      this.updateQuery({ page });
      this.$refs.listTop?.scrollIntoView({ block: "start", behavior: "smooth" });
    },
    onSearchInput(value) {
      this.search = value;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        // Una búsqueda nueva siempre empieza en la página 1.
        this.updateQuery({ search: this.search.trim(), page: 1 }, true);
      }, SEARCH_DEBOUNCE_MS);
    },
    clearSearch() {
      this.search = "";
      clearTimeout(this.searchTimer);
      this.updateQuery({ search: "", page: 1 }, true);
    },
    goToMoodleCourse(uuid) {
      this.classroomStore.open(uuid, this.$t);
    },
    changeProductStatus(productUuid, status) {
      productService
        .changeProductStatus(this.academiesStore.academy.uuid, productUuid, status)
        .then(() => {
          const product = this.products.find((item) => item.uuid === productUuid);
          if (product) {
            product.status = status ? "publish" : "private";
          }
          NotificationService.showNotification(
            this.$t(status ? "products.product_published" : "products.product_unpublished"),
            "success"
          );
        })
        .catch((error) => {
          NotificationService.showApiError(error, this.$t("products.unexpected_error"));
        });
    },
  },
};
</script>
